// src/features/pdf/utils/pdfUtils.ts
import { PDFDocument, PDFString, StandardFonts, rgb } from 'pdf-lib';
import JSZip from 'jszip';
import { saveAs } from 'file-saver';
import { OutputFile } from '../components/ToolActionArea';

export interface CompressOptions {
    quality: 'recommended' | 'high' | 'custom';
    // Custom options
    useObjectStreams?: boolean;
    removeMetadata?: boolean;
    // Note: Image re-compression is a highly complex task.
    // For this implementation, we will rely on pdf-lib's standard optimization
    // features which are effective but may not drastically reduce image-heavy PDFs
    // without a full image processing pipeline.
}

/**
 * Interface representing the state of a single page for reordering.
 */
export interface ReorderablePage {
    id: string;
    originalIndex: number;
    deleted: boolean;
}

/**
 * Merges multiple PDF files into a single PDF.
 * @param files - An array of File objects to be merged.
 * @returns A Promise that resolves with a Uint8Array of the merged PDF.
 */
export const mergePdfs = async (files: File[]): Promise<Uint8Array> => {
    // Create a new PDFDocument
    const mergedPdf = await PDFDocument.create();

    for (const file of files) {
        // Read the file into an ArrayBuffer
        const arrayBuffer = await file.arrayBuffer();
        // Load the PDF from the ArrayBuffer
        const pdf = await PDFDocument.load(arrayBuffer);

        // Copy all pages from the source PDF to the merged PDF
        const copiedPages = await mergedPdf.copyPages(pdf, pdf.getPageIndices());
        copiedPages.forEach((page) => mergedPdf.addPage(page));
    }

    // Serialize the PDFDocument to bytes (a Uint8Array)
    const mergedPdfBytes = await mergedPdf.save();
    return mergedPdfBytes;
};


/**
 * Splits a PDF into multiple documents based on ranges or single pages.
 * @param file - The PDF file to split.
 * @param splitMode - The mode of splitting: 'all' or 'custom'.
 * @param customRanges - A string representing custom ranges, e.g., "1, 3-5, 8".
 * @returns A Promise that resolves with an array of OutputFile objects.
 */
export const splitPdf = async (file: File, splitMode: 'all' | 'custom', customRanges: string): Promise<OutputFile[]> => {
    const arrayBuffer = await file.arrayBuffer();
    const sourcePdf = await PDFDocument.load(arrayBuffer);
    const totalPages = sourcePdf.getPageCount();
    const outputPdfs: OutputFile[] = [];

    const pageIndicesToExtract: number[][] = [];

    if (splitMode === 'all') {
        // Create a single-page PDF for each page
        for (let i = 0; i < totalPages; i++) {
            pageIndicesToExtract.push([i]);
        }
    } else {
        // Parse custom ranges
        const ranges = customRanges.split(',').map(s => s.trim()).filter(Boolean);
        for (const range of ranges) {
            if (range.includes('-')) {
                const [start, end] = range.split('-').map(Number);
                const indices = [];
                for (let i = start - 1; i < Math.min(end, totalPages); i++) {
                    indices.push(i);
                }
                if (indices.length > 0) pageIndicesToExtract.push(indices);
            } else {
                const pageNum = Number(range);
                if (pageNum > 0 && pageNum <= totalPages) {
                    pageIndicesToExtract.push([pageNum - 1]);
                }
            }
        }
    }

    if (pageIndicesToExtract.length === 0) {
        throw new Error('No valid pages selected for splitting.');
    }

    // Process each set of pages into a new PDF
    for (const indices of pageIndicesToExtract) {
        const newPdf = await PDFDocument.create();
        const copiedPages = await newPdf.copyPages(sourcePdf, indices);
        copiedPages.forEach(page => newPdf.addPage(page));

        const bytes = await newPdf.save();
        const pageNumbers = indices.map(i => i + 1).join('-');

        // Use the original filename as a base
        const baseName = file.name.replace(/\.pdf$/i, '');

        outputPdfs.push({
            name: `${baseName}-pages-${pageNumbers}.pdf`,
            data: bytes,
            size: bytes.length,
        });
    }

    return outputPdfs;
};

/**
 * Compresses a PDF file by rebuilding it and applying optimizations.
 * @param file - The PDF file to compress.
 * @param options - The compression options.
 * @returns A Promise that resolves with the compressed OutputFile object.
 */
export const compressPdf = async (file: File, options: CompressOptions): Promise<OutputFile> => {
    const arrayBuffer = await file.arrayBuffer();

    // Load the original PDF
    const pdfDoc = await PDFDocument.load(arrayBuffer, {
        // This is important for handling complex or malformed PDFs
        updateFieldAppearances: false,
    } as any);

    // NOTE: In a more advanced implementation, one would iterate through PDF image objects (XObjects),
    // extract them, compress them using a library like sharp (in Node.js) or canvas (in browser),
    // and then re-embed them. This is a very complex process.
    //
    // For this version, we rely on the optimizations done by `pdf-lib` when saving the document.
    // Using object streams is a key feature for PDF size reduction.

    const useObjectStreams = options.quality !== 'high'; // 'high' quality means less compression

    // Serialize the PDFDocument to bytes with object stream compression
    const compressedPdfBytes = await pdfDoc.save({
        useObjectStreams: useObjectStreams,
    });

    const baseName = file.name.replace(/\.pdf$/i, '');
    const newFileName = `${baseName}-compressed.pdf`;

    return {
        name: newFileName,
        data: compressedPdfBytes,
        size: compressedPdfBytes.length,
    };
};

/**
 * Encrypts a PDF file with a user-provided password.
 * @param file - The PDF file to encrypt.
 * @param password - The password to use for encryption.
 * @returns A Promise that resolves with the encrypted PDF as a Uint8Array.
 */
export const encryptPdf = async (file: File, password: string): Promise<Uint8Array> => {
    const arrayBuffer = await file.arrayBuffer();
    const pdfDoc = await PDFDocument.load(arrayBuffer, { ignoreEncryption: true });

    // pdf-lib encrypts the document on save when a password is provided
    const encryptedBytes = await pdfDoc.save({
        userPassword: password,
        ownerPassword: password, // Use the same for simplicity or make it configurable
    } as any);

    return encryptedBytes;
};

/**
 * Decrypts a PDF file with a user-provided password.
 * @param file - The PDF file to decrypt.
 * @param password - The password to unlock the document.
 * @returns A Promise that resolves with the decrypted PDF as a Uint8Array.
 * @throws An error if the password is incorrect.
 */
export const decryptPdf = async (file: File, password: string): Promise<Uint8Array> => {
    const arrayBuffer = await file.arrayBuffer();
    try {
        // Attempt to load the document with the provided password.
        // This will throw an error if the password is wrong.
        const pdfDoc = await PDFDocument.load(arrayBuffer, {
            ownerPassword: password,
        } as any);

        // Re-save the document without a password to effectively remove encryption.
        const decryptedBytes = await pdfDoc.save();
        return decryptedBytes;
    } catch (e) {
        // Catch common password errors from pdf-lib and throw a user-friendly message
        if (e instanceof Error && (e.message.includes('password') || e.message.includes('encrypted'))) {
            throw new Error('pdf_decrypt_error_wrong_password');
        }
        // Rethrow other unexpected errors
        throw e;
    }
};

/**
 * Reorders and/or deletes pages from a PDF file.
 * @param file - The source PDF file.
 * @param pageOrder - An array of page objects indicating the new order and deletion status.
 * @returns A Promise that resolves with a Uint8Array of the new PDF.
 */
export const reorderPdfPages = async (file: File, pageOrder: ReorderablePage[]): Promise<Uint8Array> => {
    const arrayBuffer = await file.arrayBuffer();
    const sourcePdf = await PDFDocument.load(arrayBuffer);
    const newPdf = await PDFDocument.create();

    // Create a list of original page indices to copy, filtering out deleted pages
    const indicesToCopy = pageOrder
        .filter(p => !p.deleted)
        .map(p => p.originalIndex);

    if (indicesToCopy.length === 0) {
        throw new Error('All pages have been deleted. Cannot create an empty PDF.');
    }

    // Copy the pages from the source document into the new document in the desired order
    const copiedPages = await newPdf.copyPages(sourcePdf, indicesToCopy);
    copiedPages.forEach(page => newPdf.addPage(page));

    // Serialize the new PDFDocument to bytes
    return await newPdf.save();
};

/**
 * Extracts a single page from a PDF to be used for preview.
 * @param file - The source PDF file.
 * @param pageIndex - The zero-based index of the page to extract.
 * @returns A Promise that resolves with a Uint8Array of the single-page PDF.
 */
export const extractSinglePageForPreview = async (file: File, pageIndex: number): Promise<Uint8Array> => {
    const arrayBuffer = await file.arrayBuffer();
    const sourcePdf = await PDFDocument.load(arrayBuffer);
    const newPdf = await PDFDocument.create();

    if (pageIndex < 0 || pageIndex >= sourcePdf.getPageCount()) {
        throw new Error('Page index out of bounds.');
    }

    const [copiedPage] = await newPdf.copyPages(sourcePdf, [pageIndex]);
    newPdf.addPage(copiedPage);

    return await newPdf.save();
};


/**
 * Helper function to download a single file.
 * @param data - The file data as a Uint8Array.
 * @param fileName - The name of the file to be saved.
 * @param mimeType - The MIME type of the file.
 */
export const downloadFile = (data: Uint8Array, fileName: string, mimeType: string = 'application/pdf') => {
    const blob = new Blob([data], { type: mimeType });
    saveAs(blob, fileName);
};


/**
 * Helper function to download multiple files as a ZIP.
 * @param files - An array of OutputFile objects.
 * @param zipName - The desired name for the ZIP file.
 */
export const downloadFilesAsZip = async (files: OutputFile[], zipName: string) => {
    const zip = new JSZip();
    files.forEach(file => {
        zip.file(file.name, file.data);
    });
    const content = await zip.generateAsync({ type: 'blob' });
    saveAs(content, zipName);
};
